import { PartialWithId } from '@inmagik/react-crud/dist/types'
import classNames from 'classnames'
import { FormikHelpers } from 'formik'
import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { FiltriBilance } from '../../../components/FiltriBilance'
import ModalForm from '../../../components/ModalForm'
import Paginator from '../../../components/Paginator'
import TabMenu from '../../../components/TabMenu'
import { USER_ROLES } from '../../../constants'
import { useBilanceList } from '../../../hooks/bilance'
import { useCliente } from '../../../hooks/clienti'
import { useQsFilters } from '../../../hooks/filters'
import { useSediList, useUploadDVR } from '../../../hooks/sedi'
import { useCreateUtente, useUpdateUtente } from '../../../hooks/utenti'
import { Sede, User } from '../../../types'
import { transformErrorForForm } from '../../../utils/utils'
import BilanceTable from '../../Bilance/BilanceTable'
import ListGroupFilters from '../../Bilance/ListGroupFilters'
import ModalFiltersBilancia from '../../BilanciaDettaglio/ModalFiltersBilancia'
import FormUtenteCliente from '../../Utenti/FormUtenteCliente'
import FormDVR from '../FormDVR'
import SediTable from '../SediTable'
import { AreaClientiRedirect } from '../../AreaClientiRedirect'
import MobileDetails from '../../../components/MobileDetails'

const schemaSede = yup.object().shape({
  dvr: yup
    .mixed()
    .required('Il DVR è obbligatorio')
    .test('fileType', 'Il file deve essere un PDF', (value) => {
      if (!value) return false // già gestito dal required
      if (!(value instanceof File)) return false
      return value.type === 'application/pdf'
    })
    .label('DVR'),
})

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get('page') ?? 1),
  search: params.get('search') ?? '',
  ordering: params.get('ordering') ?? 'ragione_sociale',
  tipo_bilancia: params.get('tipo_bilancia') ?? '',
  sede: params.get('sede') ?? '',
  verif_periodica: params.get('verif_periodica') ?? '',
  rapporto_taratura: params.get('rapporto_taratura') ?? '',
  data_scadenza_periodica__gte: params.get('data_scadenza_periodica__gte') ?? '',
  data_scadenza_periodica__lte: params.get('data_scadenza_periodica__lte') ?? '',
  data_scadenza_taratura__gte: params.get('data_scadenza_taratura__gte') ?? '',
  data_scadenza_taratura__lte: params.get('data_scadenza_taratura__lte') ?? '',
  con_richiesta_periodica: params.get('con_richiesta_periodica') ?? '',
  data_richiesta_periodica__gte: params.get('data_richiesta_periodica__gte') ?? '',
  data_richiesta_periodica__lte: params.get('data_richiesta_periodica__lte') ?? '',
  data_intervento_periodica_aperto__isnull: params.get('data_intervento_periodica_aperto__isnull') ?? '',
  data_intervento_periodica_aperto__gte: params.get('data_intervento_periodica_aperto__gte') ?? '',
  data_intervento_periodica_aperto__lte: params.get('data_intervento_periodica_aperto__lte') ?? '',
  data_intervento_taratura_aperto__isnull: params.get('data_intervento_taratura_aperto__isnull') ?? '',
  data_intervento_taratura_aperto__gte: params.get('data_intervento_taratura_aperto__gte') ?? '',
  data_intervento_taratura_aperto__lte: params.get('data_intervento_taratura_aperto__lte') ?? '',
  fuori_uso: params.get('fuori_uso') ?? '',
  dismessa: params.get('dismessa') ?? '',
})

const initialValuesUtente = {
  full_name: '',
  email: '',
  role: USER_ROLES.responsabile_cliente.value,
}

const schemaUtente = yup.object().shape({
  full_name: yup.string().required().label('Nome e Cognome'),
  email: yup.string().email().required().label('Email'),
  role: yup.string().required().label('Ruolo'),
  // clienti: yup.array().of(yup.number()).min(1, 'Seleziona almeno un cliente').label('Aziende'),
})

export default function ClienteDettaglioAreaClienti() {
  const { filters, uiFilters, setFilters } = useQsFilters(initFilters)
  const { id } = useParams()
  const cliente = useCliente(String(id))
  const { data: bilance, isLoading } = useBilanceList({
    _cliente: String(id),
    page: String(filters.page),
    search: filters.search,
    ordering: filters.ordering,
    tipo_bilancia: filters.tipo_bilancia,
    sede: filters.sede,
    verif_periodica: filters.verif_periodica,
    rapporto_taratura: filters.rapporto_taratura,
    data_scadenza_periodica__gte: filters.data_scadenza_periodica__gte,
    data_scadenza_periodica__lte: filters.data_scadenza_periodica__lte,
    data_scadenza_taratura__gte: filters.data_scadenza_taratura__gte,
    data_scadenza_taratura__lte: filters.data_scadenza_taratura__lte,
    con_richiesta_periodica: filters.con_richiesta_periodica,
    data_richiesta_periodica__gte: filters.data_richiesta_periodica__gte,
    data_richiesta_periodica__lte: filters.data_richiesta_periodica__lte,
    data_intervento_periodica_aperto__isnull: filters.data_intervento_periodica_aperto__isnull,
    data_intervento_periodica_aperto__gte: filters.data_intervento_periodica_aperto__gte,
    data_intervento_periodica_aperto__lte: filters.data_intervento_periodica_aperto__lte,
    data_intervento_taratura_aperto__isnull: filters.data_intervento_taratura_aperto__isnull,
    data_intervento_taratura_aperto__gte: filters.data_intervento_taratura_aperto__gte,
    data_intervento_taratura_aperto__lte: filters.data_intervento_taratura_aperto__lte,
    fuori_uso: filters.fuori_uso,
    dismessa: filters.dismessa,
  })
  const sedi = useSediList({
    cliente: String(id),
    page: String(filters.page),
    search: filters.search,
    ordering: filters.ordering,
  })
  const [tab, setTab] = useState<'bilance' | 'sedi'>('bilance')
  const [openFilters, setOpenFilters] = useState(false)
  const [modalFiltersState, modalFiltersActions] = useModalTrigger()
  const [modalStateUtenteForm, modalActionsUtenteForm] = useModalTrigger<User>()
  const [modalStateUploadDVRSede, modalActionsUploadDVRSede] = useModalTrigger<Sede>()

  const { mutateAsync: createUser } = useCreateUtente()
  const { mutateAsync: updateUser } = useUpdateUtente()

  const saveCustomer = useCallback(
    (utente: PartialWithId<User>, actions: FormikHelpers<User>) => {
      if (utente.id) {
        return updateUser(utente)
          .then(() => {
            modalActionsUtenteForm.close()
            actions.resetForm()
          })
          .catch((e) => {
            actions.setErrors(e)
          })
      } else {
        return createUser(utente)
          .then(() => {
            modalActionsUtenteForm.close()
            actions.resetForm()
          })
          .catch((e) => {
            actions.setErrors(e.data)
          })
      }
    },
    [modalActionsUtenteForm, modalStateUtenteForm, updateUser, createUser]
  )

  const areFiltersActive = useMemo(() => {
    const { page, search, ordering, ...rest } = filters
    return Object.values(rest).some((v) => v !== '')
  }, [filters])

  const filtersListGroupRef = useRef<HTMLDivElement>(null)

  const { mutateAsync: uploadDVR } = useUploadDVR()
  const saveDVR = useCallback(
    (sede: PartialWithId<{ dvr: File; id: Sede['id'] }>, actions: FormikHelpers<{ dvr: File; id: Sede['id'] }>) => {
      if (!sede.dvr) {
        actions.setErrors({ dvr: 'Il DVR è obbligatorio' })
        return Promise.reject()
      } else {
        return uploadDVR({ sedeId: sede.id!, dvr: sede.dvr as File })
          .then(() => {
            modalActionsUploadDVRSede.close()
            actions.resetForm()
            toast.success('DVR caricato con successo', {
              position: 'bottom-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            })
            sedi.refetch()
          })
          .catch((e) => {
            actions.setErrors(transformErrorForForm(e))
          })
      }
    },
    [uploadDVR, modalActionsUploadDVRSede]
  )

  // click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filtersListGroupRef.current && !filtersListGroupRef.current.contains(event.target as Node)) {
        setOpenFilters(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [filtersListGroupRef])

  const navigate = useNavigate()

  if (cliente.isError) {
    return <AreaClientiRedirect message="Pagina non disponibile" />
  }

  return (
    <div className="page page-list">
      <div className="row mt-4">
        <div className="col-12">
          <div className="d-flex align-items-center">
            <div
              onClick={() => {
                navigate(-1)
              }}
              className="p-sm no-link d-flex align-items-center pointer"
            >
              <span className="material-icons text-secondary">groups</span>
              <span className="ms-2">Lista clienti</span>
            </div>
            <span className="material-icons text-secondary">chevron_right</span>
            <div className="ms-2 p-sm bg-secondary-light p-2 text-secondary rounded">Dettaglio cliente</div>
          </div>
        </div>
        <div className="col-12 d-flex justify-content-between">
          <div className="d-flex align-items-center">
            <div>
              <h1 className="title mt-2 me-4">{cliente.data?.ragione_sociale}</h1>
            </div>
          </div>
        </div>
        <div className="col-12">
          <MobileDetails>
            <div className="d-flex align-items-center flex-wrap column-gap-4">
              <div className="p-md">
                <span className="text-body-secondary">Numero rea:</span>
                <span className="ms-2">{cliente.data?.sede_principale?.numero_rea}</span>
              </div>
              <div className="p-md">
                <span className="text-body-secondary">Partita iva:</span>
                <span className="ms-2">{cliente.data?.sede_principale?.partita_iva}</span>
              </div>
              <div className="p-md">
                <span className="text-body-secondary">Numero sedi:</span>
                <span className="ms-2">{cliente.data?.numero_sedi}</span>
              </div>
            </div>
            <div className="d-flex align-items-center flex-wrap p-md mt-2">
              <span>{cliente.data?.sede_principale?.toponimo_data?.nome} </span>
              <span className="ms-1">
                {cliente.data?.sede_principale?.via}
                {', '}{' '}
              </span>
              <span className="ms-1">{cliente.data?.sede_principale?.civico}</span>
              <span className="ms-2">-</span>
              <span className="ms-2">
                {cliente.data?.sede_principale?.comune_data?.nome} {cliente.data?.sede_principale?.comune_data?.cap}
              </span>
              <span className="ms-2">{cliente.data?.sede_principale?.provincia_data?.sigla}</span>
            </div>
          </MobileDetails>
        </div>
        <div className="col-12 mt-1">
          <div className="d-flex align-items-center justify-content-between list-header">
            <TabMenu
              tabs={[
                { label: 'Bilance', value: 'bilance' },
                { label: 'Sedi', value: 'sedi' },
              ]}
              activeTab={tab}
              onTabChange={setTab}
            />
            <div className="d-flex align-items-center list-header-actions"></div>
          </div>
        </div>
        {tab === 'bilance' && (
          <div className="col-12 mt-2">
            <div className="bg-white p-2 border-bottom d-flex justify-content-between align-items-center w-100 rounded list-toolbar">
              <div className="ms-2 d-flex align-items-center list-filters list-filters-inline">
                <input
                  type="text"
                  style={{ maxWidth: '500px' }}
                  className="form-control"
                  placeholder="Cerca per matricola..."
                  value={uiFilters.search}
                  onChange={(e) => setFilters({ search: e.target.value, page: 1 })}
                />
                <div className="ms-2 position-relative">
                  <Button
                    onClick={() => {
                      setOpenFilters(!openFilters)
                    }}
                    variant={openFilters ? 'secondary' : 'outline-secondary'}
                    className={classNames('d-flex align-items-center', {
                      'text-white': openFilters,
                    })}
                    type="button"
                  >
                    <span className="material-icons">filter_alt</span>
                    <span className="ms-2 btn-label">Filtri</span>
                  </Button>
                  <div className="position-absolute" ref={filtersListGroupRef}>
                    {openFilters && (
                      <ListGroupFilters
                        filters={filters}
                        setFilters={setFilters}
                        modalFiltersActions={modalFiltersActions}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="me-2">
                Stai visualizzando {bilance?.count ?? 0} di {bilance?.full_count ?? 0} bilance
              </div>
            </div>
            {areFiltersActive && (
              <div className="bg-white p-2 border-bottom d-flex justify-content-between align-items-center w-100 rounded list-toolbar">
                <div className="d-flex align-items-center flex-wrap gap-2 active-filters">
                  <FiltriBilance filters={filters} setFilters={setFilters} />
                </div>
                <div>
                  <Button
                    onClick={() => {
                      setFilters(initFilters(new URLSearchParams()))
                    }}
                    size="sm"
                    variant="danger"
                    className="d-flex align-items-center"
                    aria-label="Reset filtri"
                    type="button"
                  >
                    <span className="material-icons me-2">filter_alt_off</span>
                    <span className="btn-label">Reset</span>
                  </Button>
                </div>
              </div>
            )}
            <BilanceTable
              bilance={bilance}
              isLoading={isLoading}
              setFilters={setFilters}
              uiFilters={uiFilters}
              hasFiltersActive={areFiltersActive}
              showSede
            />
            <div className="position-relative">
              <Paginator
                count={bilance?.count ?? 0}
                currentPage={uiFilters.page}
                goToPage={(page) => setFilters({ page })}
              />
            </div>
          </div>
        )}
        {tab === 'sedi' && (
          <div className="col-12 mt-2">
            <div className="bg-white p-2 border-bottom d-flex justify-content-between align-items-center w-100 rounded list-toolbar">
              <div className="ms-2">
                <input
                  type="text"
                  style={{ maxWidth: '500px' }}
                  className="form-control"
                  placeholder="Cerca per sede..."
                  value={uiFilters.search}
                  onChange={(e) => setFilters({ search: e.target.value, page: 1 })}
                />
              </div>
              <div className="me-2">
                Stai visualizzando {sedi?.data?.count ?? 0} di {sedi?.data?.full_count ?? 0} sedi
              </div>
            </div>
            <SediTable
              sedi={sedi}
              setFilters={setFilters}
              uiFilters={uiFilters}
              modalActionsUploadDVR={modalActionsUploadDVRSede}
            />
            <div className="position-relative">
              <Paginator
                count={sedi?.data?.count ?? 0}
                currentPage={uiFilters.page}
                goToPage={(page) => setFilters({ page })}
              />
            </div>
          </div>
        )}
      </div>

      {modalFiltersState.isOpen && (
        <ModalFiltersBilancia
          isOpen={modalFiltersState.isOpen}
          toggle={() => {
            modalFiltersActions.close()
            setOpenFilters(false)
          }}
          setFilters={setFilters}
          filters={filters}
          cliente={cliente}
        />
      )}

      <ModalForm<User>
        title={modalStateUtenteForm.value ? 'Modifica utente' : 'Crea utente'}
        isOpen={modalStateUtenteForm.isOpen}
        toggle={modalActionsUtenteForm.toggle}
        initialValues={
          modalStateUtenteForm.value ?? {
            ...initialValuesUtente,
            clienti: [Number(id)],
          }
        }
        save={saveCustomer}
        schema={schemaUtente}
      >
        <FormUtenteCliente />
      </ModalForm>
      {modalStateUploadDVRSede.isOpen && modalStateUploadDVRSede.value && (
        <ModalForm<{ dvr: File; id: Sede['id'] }>
          title={`Carica DVR per sede ${modalStateUploadDVRSede.value.toponimo_data?.nome} ${modalStateUploadDVRSede.value.via} ${modalStateUploadDVRSede.value.civico}`}
          isOpen={modalStateUploadDVRSede.isOpen}
          toggle={modalActionsUploadDVRSede.toggle}
          initialValues={{
            dvr: modalStateUploadDVRSede.value.dvr as unknown as File,
            id: modalStateUploadDVRSede.value.id,
          }}
          save={saveDVR}
          schema={schemaSede}
          labelSave={'Salva'}
        >
          <FormDVR />
        </ModalForm>
      )}
    </div>
  )
}
